import usePrivateAxios from "@/common/api/usePrivateAxios";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { MissionsTransformer } from "../services/missionsTransformer";
import { MissionFilters } from "../types/mission.type";

const DEFAULT_MISSION_INCLUDES = [
  "establishment",
  "establishment_company_branch",
  "allowed_establishments",
  "allowed_establishment_company_branches",
  "mission_requirements",
  "requirements",
  "mission_rewards",
  "mission_rewards.rewardable",
  "rewards",
  "rewards.rewardable",
].join(",");

type UseMissionsListProps = {
  filters?: MissionFilters & { establishmentId?: string };
};

export const useMissionsList = ({ filters }: UseMissionsListProps) => {
  const privateAxios = usePrivateAxios();
  const {
    search,
    page,
    status,
    validity,
    establishmentId,
  } = filters || {};
  const missionsQueryKey = [
    "missions",
    search ?? "",
    page ?? 1,
    status ?? "",
    validity ?? "",
    establishmentId ?? "",
  ];
  const filterParams = {
    include: DEFAULT_MISSION_INCLUDES,
    search: search || undefined,
    page: page || undefined,
    'q[status_eq]': status || undefined,
    validity: validity || undefined,
    establishment_id: establishmentId || undefined,
  };

  return useQuery({
    queryKey: missionsQueryKey,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await privateAxios.get("/tmais/missions", {
        params: filterParams,
      });
      const payload = response.data?.data || response.data || [];
      const included = response.data?.included;
      const meta = response.data?.meta || response.data?.pagination || {};
      return {
        data: MissionsTransformer.listFromApi(payload, included),
        meta,
      };
    },
  });
};

export const useMission = (id?: string) => {
  const privateAxios = usePrivateAxios();

  return useQuery({
    queryKey: ["mission", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const response = await privateAxios.get(`/tmais/missions/${id}`, {
        params: {
          include: DEFAULT_MISSION_INCLUDES,
        },
      });
      const rawData = response.data?.data || response.data || {};
      const included = response.data?.included;
      return MissionsTransformer.fromApi(rawData, included);
    },
  });
};

interface UseDeleteMissionProps {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export const useDeleteMission = ({
  onSuccess,
  onError,
}: UseDeleteMissionProps = {}) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ["deleteMission"],
    mutationFn: async (id: string) => {
      await privateAxios.delete(`/tmais/missions/${id}`);
      return id;
    },
    onSuccess: () => onSuccess?.(),
    onError,
  });
};
